/**
 * Block 13 PR-G Phase 4A — Contract form presenter (ADR-0005 Container/Presenter).
 *
 * Pure function: рендерит layout shell (P-046) + 6 form sections (Phase 4A+4B+4C
 * complete) + BottomActions. View не делает I/O — Container'у dispatch
 * и persistence.
 *
 * **Layout shell pattern (P-046):**
 *   <main className="min-h-screen flex flex-col bg-canvas">
 *     <Header title="Договор" />
 *     <section className="flex-1 overflow-y-auto ..."> {content} </section>
 *     <BottomBar variant="default" />
 *   </main>
 *
 * **P-040 compliance (Phase 7 hotfix round 7, 2026-05-26):** VideoButton НЕ
 * рендерится на child screens — applies только к entry screens блока
 * (ContractListView 2.13.1). Doc title card sub-Header — child screen pattern
 * (mirror Block 12 QuotePageView без VideoButton).
 *
 * **Scroll restoration (P-047):** unified `<ScrollToTop/>` в RootLayout —
 * НЕ per-page useEffect.
 *
 * Reference: `features/calculator/quote/QuotePageView.tsx` (pattern).
 */

import type { Dispatch } from 'react';

import { Header } from '@/components/Header/Header';
import { BottomBar } from '@/components/BottomBar/BottomBar';

import { BottomActions } from './sections/BottomActions';
import { ReseedFromQuoteDialog } from './components/ReseedFromQuoteDialog';
import { SectionCostAndTerms } from './sections/SectionCostAndTerms';
import { SectionParties } from './sections/SectionParties';
import { SectionPreamble } from './sections/SectionPreamble';
import { SectionRequisites } from './sections/SectionRequisites';
import { SectionSpec } from './sections/SectionSpec';
import { SectionWarranty } from './sections/SectionWarranty';
import type { ContractAction, ContractFormState } from './state/contractReducer';

export interface ContractFormViewProps {
  state: ContractFormState;
  dispatch: Dispatch<ContractAction>;
  draftKey: string;
  /** PR-B1 (ADR-0034) — session-end clear hook, прокидывается в BottomBar «На главную». */
  onBeforeNavigateHome?: () => void;
  /**
   * PR-C2 Phase 2 (ADR-0036) — показать уведомление о частичном пересеве из
   * изменённого КП («Стоимость и спецификация обновлены из КП», решение #5).
   * PR-C5: показывается ПОСЛЕ подтверждения «Да»/«↻» (а не авто-пересева).
   */
  reseedNotice?: boolean;
  /** PR-C2 Phase 2 — закрыть уведомление (transient, не персистится). */
  onDismissReseedNotice?: () => void;
  /** PR-C5 (ADR-0039) — открыта ли модалка «Обновить данные из КП?». */
  reseedPromptOpen?: boolean;
  /** PR-C5 — «Да»: пересев родного блока из КП (также onReseedFromQuote ссылки). */
  onConfirmReseed?: () => void;
  /** PR-C5 — «Нет»/Esc/overlay: подавить модалку (бамп quotePromptAckAt). */
  onDismissReseed?: () => void;
  /** PR-C5 (Вариант 2) — КП новее последнего сева → показать ссылку «↻» в родном блоке. */
  reseedAvailable?: boolean;
}

export function ContractFormView({
  state,
  dispatch,
  draftKey,
  onBeforeNavigateHome,
  reseedNotice = false,
  onDismissReseedNotice,
  reseedPromptOpen = false,
  onConfirmReseed,
  onDismissReseed,
  reseedAvailable = false,
}: ContractFormViewProps) {
  return (
    <main className="min-h-screen flex flex-col bg-canvas">
      <Header title="Договор" />
      <section
        className="flex-1 overflow-y-auto pb-6 pt-3 flex flex-col gap-4"
        aria-label="Содержимое формы договора"
      >
        {/* Doc title card — sentence case + dedup №. Phase 6.5 (#2 + #3):
            template.title уже содержит «№____» — выводим литералом «Договор на
            изготовление мебели №{N}», без префикса template.title чтобы не было
            дублирования. City/date line убрана (#3) — эти поля редактируются в
            SectionPreamble и в самом тексте PDF, в шапке формы избыточны. */}
        <section
          data-testid="contract-doc-header"
          className="bg-card border-2 border-accent rounded-2xl shadow-sm px-4 py-3 mx-3 text-center"
          aria-labelledby="contract-doc-header-title"
        >
          <h2
            id="contract-doc-header-title"
            className="font-display text-ink text-h-logo m-0 leading-tight"
          >
            Договор на изготовление мебели №{state.contractNum || '___'}
          </h2>
        </section>

        {/* PR-C2 Phase 2 (ADR-0036) — уведомление о частичном пересеве из
            изменённого КП (решение #5). Design system v0.6: bg-card +
            border-accent (P-038), text-ink (P-039/P-041), кнопка min-h-tap
            (P-044), role="status" для a11y. Transient — закрывается, не персистится. */}
        {reseedNotice && (
          <div
            role="status"
            data-testid="contract-reseed-notice"
            className="bg-card border-2 border-accent rounded-2xl shadow-sm px-4 py-3 mx-3 flex items-center gap-3"
          >
            <p className="text-ink text-sm m-0 flex-1">Стоимость и спецификация обновлены из КП</p>
            <button
              type="button"
              onClick={onDismissReseedNotice}
              aria-label="Закрыть уведомление"
              className="min-h-tap min-w-tap text-ink text-xl leading-none px-2 shrink-0"
            >
              ✕
            </button>
          </div>
        )}

        <SectionPreamble state={state} dispatch={dispatch} />
        <SectionParties state={state} dispatch={dispatch} />
        <SectionSpec
          state={state}
          dispatch={dispatch}
          reseedAvailable={reseedAvailable}
          onReseedFromQuote={onConfirmReseed}
        />
        <SectionCostAndTerms state={state} dispatch={dispatch} />
        <SectionWarranty state={state} dispatch={dispatch} />
        <SectionRequisites state={state} dispatch={dispatch} />

        <BottomActions state={state} dispatch={dispatch} draftKey={draftKey} />
      </section>
      <BottomBar variant="default" onBeforeNavigateHome={onBeforeNavigateHome} />

      {/* PR-C5 (ADR-0039) — модалка «Обновить данные из КП?» при заходе, если
          родной КП изменился. «Да» → пересев родного блока; «Нет» → подавить. */}
      <ReseedFromQuoteDialog
        isOpen={reseedPromptOpen}
        onConfirm={onConfirmReseed ?? (() => {})}
        onCancel={onDismissReseed ?? (() => {})}
      />
    </main>
  );
}
